/** @format */

import { DOCUMENT, CommonModule } from '@angular/common';
import {
	AfterViewChecked,
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	inject,
	Input,
	OnDestroy,
	OnInit,
	ViewChild
} from '@angular/core';
import { UtilityService } from '@shared/data-access/services';
import { Store } from '@ngrx/store';
import {
	AppProductReviewState,
	InvalidResponse,
	ProductReviewAnalyticsService,
	Review,
	ReviewMedia,
	ReviewSelectedMedia,
	SelectedTagDistributionInformationFrequency,
	UtilityReviewService,
	addSliderState,
	selectCurrentReview,
	selectProductReviewsMultimedia,
	selectSelectedTag,
	setReviewOnDrawer,
	updateProductReviewsMultimedia
} from '@product-reviews/data-access';
import {
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService,
	homefieldIconSignCircledCheckOutline
} from '@dsg-tech/homefield-angular-icons';

import { ReviewBadgesComponent } from '../review-badges/review-badges.component';
import { ReviewFeaturesComponent } from '../review-features/review-features.component';
import { ReviewHelpfulComponent } from '../review-helpful/review-helpful.component';
import { ReviewMediaComponent } from '../review-media/review-media.component';
import { ReviewSliderComponent } from '../review-slider/review-slider.component';
import { ReviewStarsComponent } from '../review-stars/review-stars.component';
import { ReviewTextComponent } from '../review-text/review-text.component';
import { SkeletonLoaderDirective } from '../directive/skeleton-loader/skeleton-loader.directive';

import { Subject } from 'rxjs';
import { delay, filter, take, takeUntil } from 'rxjs/operators';
import { ReviewModalComponent } from '../review-modal/review-modal.component';
import { MediaSourceEnum } from '../models';
import { SkeletonShape } from '../models/skeleton-loader.interface';
import { HighlightKeywordDirective } from '../directive/highlight-keyword/highlight-keyword.directive';
import { DateAgoPipe } from '../pipes/date-ago.pipe';
import { SplitContentPipe } from '../pipes/split-content.pipe';
import { ReviewSecondaryRatingContainerComponent } from '../review-secondary-rating-container/review-secondary-rating-container.component';

@Component({
	selector: 'product-reviews-review-drawer-list',
	templateUrl: './review-drawer-list.component.html',
	styleUrl: './review-drawer-list.component.scss',
	imports: [
		CommonModule,
		DateAgoPipe,
		HighlightKeywordDirective,
		HomefieldIconLibraryModule,
		ReviewBadgesComponent,
		SplitContentPipe,
		ReviewFeaturesComponent,
		ReviewHelpfulComponent,
		ReviewMediaComponent,
		ReviewModalComponent,
		ReviewSecondaryRatingContainerComponent,
		ReviewSliderComponent,
		ReviewStarsComponent,
		ReviewTextComponent,
		SkeletonLoaderDirective
	]
})
export class ReviewDrawerListComponent
	implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit
{
	@Input() set reviewMedia(reviewList: Review[]) {
		this.reviewList =
			reviewList ??
			Array.from({ length: 15 }, (review: Review, index) => ({ Id: index, ...review }));
	}
	@Input() isLoading: boolean;
	@Input() reviewCount = 0;
	@Input() isResultFromSearch: boolean;
	@Input() searchFilter = '';

	private document = inject(DOCUMENT);
	private analyticsService = inject(ProductReviewAnalyticsService);
	private cdr = inject(ChangeDetectorRef);
	private readonly utilityService = inject(UtilityService);
	private store = inject(Store<AppProductReviewState>);
	private utilityReviewService = inject(UtilityReviewService);
	private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);

	reviewList: Review[];
	multimedia: ReviewMedia[] = [];
	skeletonShape = SkeletonShape;
	selectedTag = '';

	currentReview$ = this.store.select(selectCurrentReview);
	selectedTag$ = this.store.select(selectSelectedTag);

	private UNSUBSCRIBE$ = new Subject<void>();
	starsOptions = {
		width: '16px',
		height: '16px'
	};
	expandedReview: Review;

	@ViewChild(ReviewModalComponent) reviewModalComponent: ReviewModalComponent | undefined;
	reviewSelected: Review;
	selectReviewsMultimedia$ = this.store.select(selectProductReviewsMultimedia);

	mediaSource = MediaSourceEnum.DRAWER;

	constructor() {
		this.homefieldIconsLibraryService.registerIcons([homefieldIconSignCircledCheckOutline]);
	}

	ngOnInit(): void {
		this.handleSelectedTag();
	}

	ngAfterViewInit(): void {
		// Workaround to prevent the content to be rendered before the scrollable list is ready
		this.cdr.markForCheck();
	}

	ngAfterViewChecked(): void {
		this.fetchReviews();

		this.currentReview$
			.pipe(
				takeUntil(this.UNSUBSCRIBE$),
				filter((currentReviewId) => !!currentReviewId),
				delay(500)
			)
			.subscribe((currentReviewId: string) => {
				const reviewContainerElem = this.document.getElementById(`item_${currentReviewId}`);
				this.utilityService.scrollToElement(reviewContainerElem);
				// Required clean the current review Id to prevent multiple scroll
				this.store.dispatch(
					setReviewOnDrawer({
						currentReview: ''
					})
				);
			});
	}

	ngOnDestroy(): void {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	protected trackByBadge(_: number, badgeType: string): string {
		return badgeType;
	}

	expandReview(review: Review): void {
		this.expandedReview = review;
	}

	handleReview(reviewMedia: ReviewSelectedMedia): void {
		this.analyticsService.fireReviewAnalytics<{ event: string; value: string }>({
			name: 'multiUseAnalyticsEvent',
			payload: { event: 'RatingsAndReviewsInteractions', value: 'Image Expanded' }
		});

		// eslint-disable-next-line no-unused-expressions
		this.reviewModalComponent && this.reviewModalComponent.openModal();
		this.setSlider(reviewMedia);
	}

	setSlider(reviewMedia: ReviewSelectedMedia): void {
		this.reviewSelected = this.reviewList.find((review) => review.Id == reviewMedia.reviewId);

		// Adding new media not existing onto reviewsMultimedia state.
		if (!this.multimedia.some(({ id }) => id === reviewMedia.id)) {
			this.store.dispatch(
				updateProductReviewsMultimedia({ reviewMultimedia: this.reviewSelected })
			);
		}

		this.syncReviews(reviewMedia);
	}

	fetchReviews(): void {
		this.selectReviewsMultimedia$
			.pipe(takeUntil(this.UNSUBSCRIBE$))
			.subscribe((reviewsMultimedia: Review[]) => {
				this.multimedia = this.utilityReviewService.transformReviewMedia(reviewsMultimedia);
			});
	}

	syncReviews(reviewMedia: ReviewSelectedMedia): void {
		this.selectReviewsMultimedia$.pipe(take(1)).subscribe((reviewsMultimedia: Review[]) => {
			this.multimedia = this.utilityReviewService.transformReviewMedia(reviewsMultimedia);

			const imageIndex = this.multimedia.findIndex(({ id }) => id === reviewMedia.id);
			const prevIndex = imageIndex - 1;
			const nextIndex = imageIndex + 1;

			this.store.dispatch(
				addSliderState({
					slider: {
						media: reviewMedia,
						review: this.reviewSelected,
						position: {
							previous: prevIndex > 0 ? prevIndex : null,
							current: imageIndex,
							next:
								this.multimedia?.length > nextIndex
									? nextIndex
									: this.multimedia?.length
						}
					}
				})
			);
		});
	}

	handleSelectedTag() {
		this.selectedTag$
			.pipe(
				takeUntil(this.UNSUBSCRIBE$),
				filter((tag) => !(tag instanceof InvalidResponse))
			)
			.subscribe((tag: SelectedTagDistributionInformationFrequency) => {
				this.selectedTag = tag.selectedFrequency.name;
			});
	}

	/*
	 * Track reviews changed
	 */
	trackByReview(index: number, review: Review): string {
		return index.toString() + review.Id;
	}

	/*
	 * Track media changed
	 */
	trackByMedia(index: number, media: ReviewMedia): string {
		return index.toString() + media.id;
	}
}
